import type { Error as ErrorEnvelope } from '@/lib/api/generated/model';

/**
 * The refusals the llm-profiles endpoints can answer with, one class each.
 *
 * `provider`/`base_url`/`credential_secret_key`/test-workspace refusals are
 * gone (spec 084-ai-providers-admin) — those fields no longer exist on this
 * entity, replaced by `ai_provider_id`. `LlmProfileDefaultForModelError` is
 * new (FR-015a).
 */

export type LlmProfileErrorField =
  | 'slug'
  | 'name'
  | 'model'
  | 'effort'
  | 'max_turns'
  | 'description'
  | 'ai_provider_id'
  | 'form';

class LlmProfileError extends Error {
  readonly status: number;
  readonly field: LlmProfileErrorField;

  constructor(
    name: string,
    status: number,
    field: LlmProfileErrorField,
    message: string,
  ) {
    super(message);
    this.name = name;
    this.status = status;
    this.field = field;
  }
}

/** The slug is not lowercase kebab-case, is empty, or is over length. */
export class LlmProfileSlugInvalidError extends LlmProfileError {
  constructor(message?: string) {
    super(
      'LlmProfileSlugInvalidError',
      422,
      'slug',
      message ??
        'Use lowercase letters, digits and single hyphens, with no leading, trailing, or doubled hyphen.',
    );
  }
}

/** The organization already has a profile with this slug. */
export class LlmProfileSlugConflictError extends LlmProfileError {
  constructor(message?: string) {
    super(
      'LlmProfileSlugConflictError',
      409,
      'slug',
      message ?? 'A profile with that slug already exists.',
    );
  }
}

/** The name is empty after trimming, or over length. */
export class LlmProfileNameInvalidError extends LlmProfileError {
  constructor(message?: string) {
    super(
      'LlmProfileNameInvalidError',
      422,
      'name',
      message ?? 'Enter a name, up to 100 characters.',
    );
  }
}

/** The model is empty after trimming. */
export class LlmProfileModelInvalidError extends LlmProfileError {
  constructor(message?: string) {
    super(
      'LlmProfileModelInvalidError',
      422,
      'model',
      message ?? 'Enter a model.',
    );
  }
}

/** Effort was explicitly cleared on an update. */
export class LlmProfileEffortInvalidError extends LlmProfileError {
  constructor(message?: string) {
    super(
      'LlmProfileEffortInvalidError',
      422,
      'effort',
      message ?? 'Choose an effort level.',
    );
  }
}

/** `ai_provider_id` is missing, or does not reference an existing AI Provider (spec FR-011). */
export class LlmProfileAiProviderInvalidError extends LlmProfileError {
  constructor(message?: string) {
    super(
      'LlmProfileAiProviderInvalidError',
      422,
      'ai_provider_id',
      message ?? 'Choose an AI Provider.',
    );
  }
}

/** The profile is gone — deleted from another tab, or by someone else. */
export class LlmProfileNotFoundError extends LlmProfileError {
  constructor(message?: string) {
    super(
      'LlmProfileNotFoundError',
      404,
      'form',
      message ?? 'That profile no longer exists. The list has been refreshed.',
    );
  }
}

/**
 * At least one workflow step in this workspace still references this profile.
 */
export class LlmProfileInUseError extends LlmProfileError {
  constructor(message?: string) {
    super(
      'LlmProfileInUseError',
      409,
      'form',
      message ??
        'This profile is still referenced by at least one workflow step, so it cannot be deleted. Repoint those steps to another profile first.',
    );
  }
}

/** The description is over 200 characters, or spans more than one line (spec 214, service 168). */
export class LlmProfileDescriptionInvalidError extends LlmProfileError {
  constructor(message?: string) {
    super(
      'LlmProfileDescriptionInvalidError',
      422,
      'description',
      message ?? 'Keep the description to one line, up to 200 characters.',
    );
  }
}

/**
 * This profile is the current default for its model, and other profiles for
 * that model still exist (spec FR-015a).
 */
export class LlmProfileDefaultForModelError extends LlmProfileError {
  constructor(message?: string) {
    super(
      'LlmProfileDefaultForModelError',
      409,
      'form',
      message ??
        'This is the default profile for its model, so it cannot be deleted. Set a different profile as default for this model first.',
    );
  }
}

/** A refusal this build does not recognise. Form-level and deliberately vague. */
export class LlmProfileRequestFailedError extends LlmProfileError {
  constructor(status: number, message?: string) {
    super(
      'LlmProfileRequestFailedError',
      status,
      'form',
      message ?? 'The request failed. Please try again.',
    );
  }
}

const BY_CODE: Record<string, (message?: string) => LlmProfileError> = {
  LLM_PROFILE_SLUG_INVALID: (m) => new LlmProfileSlugInvalidError(m),
  LLM_PROFILE_SLUG_CONFLICT: (m) => new LlmProfileSlugConflictError(m),
  LLM_PROFILE_NAME_INVALID: (m) => new LlmProfileNameInvalidError(m),
  LLM_PROFILE_MODEL_INVALID: (m) => new LlmProfileModelInvalidError(m),
  LLM_PROFILE_EFFORT_INVALID: (m) => new LlmProfileEffortInvalidError(m),
  LLM_PROFILE_DESCRIPTION_INVALID: (m) =>
    new LlmProfileDescriptionInvalidError(m),
  LLM_PROFILE_AI_PROVIDER_INVALID: (m) =>
    new LlmProfileAiProviderInvalidError(m),
  LLM_PROFILE_NOT_FOUND: (m) => new LlmProfileNotFoundError(m),
  LLM_PROFILE_IN_USE: (m) => new LlmProfileInUseError(m),
  profile_is_default_for_model: (m) => new LlmProfileDefaultForModelError(m),
  LLM_PROFILE_DEFAULT_FOR_MODEL: (m) => new LlmProfileDefaultForModelError(m),
};

/**
 * Map one error response to its typed refusal.
 *
 * The service's own `message` is deliberately **not** passed through — see
 * `secrets/errors.ts`'s identical note. An unrecognised code produces
 * {@link LlmProfileRequestFailedError} instead of throwing.
 */
export function toLlmProfileError(
  status: number,
  body: unknown,
): LlmProfileError {
  const code =
    typeof body === 'object' &&
    body !== null &&
    ('error' in body || 'code' in body)
      ? ((body as ErrorEnvelope).error ??
        (body as Record<string, unknown>).code)
      : undefined;

  const construct = typeof code === 'string' ? BY_CODE[code] : undefined;
  return construct ? construct() : new LlmProfileRequestFailedError(status);
}

/** Narrows a mutation's `unknown` error to a typed refusal (constitution §2.5). */
export function isLlmProfileError(error: unknown): error is LlmProfileError {
  return error instanceof LlmProfileError;
}

export type { LlmProfileError };
